import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { getNavItemsForRole } from '../../utils/permissions';
import {
  LayoutDashboard, FolderKanban, CheckSquare, Users, UserPlus,
  Handshake, Clock, Activity, LogOut, ChevronLeft, ChevronRight, UserCog, Bell, Check, CalendarDays, CheckCircle, Moon, Sun, MessageCircle, MessageSquare, Menu, X, Settings, ClipboardList, PieChart, Bot, Link
} from 'lucide-react';
import API, { notificationsAPI, usersAPI } from '../../services/api';
import { Button, Modal, FormField } from '../ui';
import './layout.css';

const iconMap = {
  dashboard: LayoutDashboard,
  ai: Bot,
  projects: FolderKanban,
  tasks: CheckSquare,
  board: FolderKanban,
  availability: CalendarDays,
  sprints: CheckCircle,
  customers: Users,
  leads: UserPlus,
  deals: Handshake,
  timelogs: Clock,
  activities: Activity,
  users: UserCog,
  chat: MessageCircle,
  notifications: Bell,
 
  feedback: MessageSquare,
  project_activity_log: ClipboardList,
  reports: PieChart,
  webhooks: Link,
};

// Breakpoint below which the sidebar switches from "collapsible rail" to
// "hidden by default / off-canvas overlay" behavior.
const MOBILE_BREAKPOINT = 768;

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const [theme, setTheme] = useState(() => localStorage.getItem('pm-theme') || 'dark');
  const { user, logout } = useAuth();
  const { unreadCount: unreadChatCount } = useChat();
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const navigate = useNavigate();
  const navItems = getNavItemsForRole(user?.role || 'Member');

  const handleLogout = () => { logout(); navigate('/login'); };

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', title: '', avatar: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', email: user.email || '', title: user.title || '', avatar: user.avatar || '' });
      
      // Fetch unread notifications count
      const fetchUnreadNotifs = async () => {
        try {
          const res = user.role === 'Admin' 
            ? await notificationsAPI.getAll() 
            : await notificationsAPI.list();
          const data = res.data?.data || [];
          // For Admin getAll returns ALL users' notifications, so filter by user_id
          // For others, the API already filters, but we still filter to be safe
          const userNotifs = user.role === 'Admin'
            ? data.filter(n => Number(n.user_id) === Number(user.user_id))
            : data;
          const unread = userNotifs.filter(n => !n.read && n.read !== 1 && n.read !== '1').length;
          setUnreadNotifCount(unread);
        } catch (err) {
          console.error('Failed to fetch unread notifications:', err);
        }
      };
      
      fetchUnreadNotifs();
      // Re-fetch periodically
      const intervalId = setInterval(fetchUnreadNotifs, 30000);
      return () => clearInterval(intervalId);
    }
  }, [user]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      let finalAvatar = profileForm.avatar;
      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        const res = await API.post('/uploads/single', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalAvatar = res.data.data.url;
      }
      await usersAPI.update(user.user_id, { ...profileForm, avatar: finalAvatar });
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating profile');
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('pm-theme', theme);
  }, [theme]);

  // Track viewport size so we know whether the sidebar should behave as a
  // collapsible desktop rail or a hidden-by-default mobile overlay. Also
  // resets any "open" mobile state when the viewport grows past the
  // breakpoint, so the sidebar can't get stuck in an overlay layout on
  // desktop after a resize.
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handleChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setMobileOpen(false);
    };
    handleChange(mql);
    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else mql.addListener(handleChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handleChange);
      else mql.removeListener(handleChange);
    };
  }, []);

  // Lock background scroll while the mobile sidebar overlay is open.
  useEffect(() => {
    if (isMobile && mobileOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [isMobile, mobileOpen]);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setMobileOpen((open) => !open);
    } else {
      setCollapsed((c) => !c);
    }
  };

  const closeMobileSidebar = () => {
    if (isMobile) setMobileOpen(false);
  };

  const sidebarClass = [
    'app-sidebar',
    collapsed && !isMobile ? 'app-sidebar--collapsed' : '',
    isMobile ? 'app-sidebar--mobile' : '',
    isMobile && mobileOpen ? 'app-sidebar--mobile-open' : '',
  ].filter(Boolean).join(' ');

  // On mobile the sidebar is either fully off-canvas or fully open (no
  // icon-only collapsed state), so labels/text should always render when
  // we're in the mobile overlay.
  const showLabels = isMobile ? true : !collapsed;

  return (
    <div className="app-shell app-shell--responsive">
      {/* Embedded responsive/interactive styles — kept local to this
          component on purpose so no other files need to change. */}
      <style>{`
        .app-shell--responsive .app-mobile-topbar {
          display: none;
        }

        .app-shell--responsive .app-sidebar-backdrop {
          display: none;
        }

        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          .app-shell--responsive {
            display: block;
            height: 100vh;
            overflow: hidden;
          }

          .app-shell--responsive .app-mobile-topbar {
            display: flex;
            align-items: center;
            gap: 12px;
            height: 56px;
            padding: 0 14px;
            border-bottom: 1px solid var(--border);
            background: var(--bg-card);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            position: sticky;
            top: 0;
            z-index: 60;
          }

          .app-shell--responsive .app-mobile-topbar__toggle {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            border: 1px solid var(--border);
            background: var(--bg-hover);
            color: var(--text);
            display: grid;
            place-items: center;
            cursor: pointer;
            flex-shrink: 0;
          }

          .app-shell--responsive .app-mobile-topbar__title {
            font-family: var(--font-display);
            font-weight: 800;
            font-size: 14px;
            color: var(--text);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .app-shell--responsive .app-sidebar.app-sidebar--mobile {
            position: fixed;
            top: 0;
            left: 0;
            height: 100vh;
            width: min(280px, 84vw) !important;
            max-width: 84vw;
            transform: translateX(-100%);
            transition: transform 0.28s ease;
            z-index: 100;
            box-shadow: none;
          }

          .app-shell--responsive .app-sidebar.app-sidebar--mobile-open {
            transform: translateX(0);
            box-shadow: 0 0 48px rgba(0, 0, 0, 0.55);
          }

          .app-shell--responsive .app-sidebar--mobile .app-sidebar__brand-text,
          .app-shell--responsive .app-sidebar--mobile .app-sidebar__link span,
          .app-shell--responsive .app-sidebar--mobile .app-sidebar__user {
            display: revert;
          }

          .app-shell--responsive .app-sidebar--mobile .app-sidebar__collapse {
            display: none;
          }

          .app-shell--responsive .app-sidebar-backdrop {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 90;
            animation: app-sidebar-fade 0.2s ease;
          }

          .app-shell--responsive .app-main {
            height: calc(100vh - 56px);
            width: 100%;
            max-width: 100vw;
            overflow-x: hidden;
          }

          .app-shell--responsive .app-main__content {
            padding: 16px 12px 28px;
            max-width: 100%;
            box-sizing: border-box;
          }

          .app-shell--responsive .app-profile-menu {
            max-width: calc(100vw - 32px);
          }

          .app-shell--responsive .app-notifications {
            width: min(360px, calc(100vw - 24px)) !important;
          }

          /* Generic safety net so any container/grid/table rendered inside
             this layout's main content area can't force horizontal
             scroll on small screens. */
          .app-shell--responsive .app-main__content table {
            display: block;
            max-width: 100%;
            overflow-x: auto;
          }

          .app-shell--responsive .app-main__content img,
          .app-shell--responsive .app-main__content video {
            max-width: 100%;
            height: auto;
          }
        }

        @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
          .app-shell--responsive {
            display: flex;
            height: 100vh;
            overflow: hidden;
          }
        }

        @keyframes app-sidebar-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Mobile-only top bar with the sidebar toggle. Hidden on desktop
          via the embedded media query above. */}
      <div className="app-mobile-topbar">
        <button
          type="button"
          className="app-mobile-topbar__toggle"
          onClick={handleSidebarToggle}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        <span className="app-mobile-topbar__title">ProjectHub</span>
      </div>

      {/* Backdrop for the mobile overlay sidebar; clicking it closes the sidebar. */}
      {isMobile && mobileOpen && (
        <div
          className="app-sidebar-backdrop"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}

      <aside className={sidebarClass}>
        <div className="app-sidebar__brand">
          <div className="app-sidebar__logo">P</div>
          {showLabels && (
            <div className="app-sidebar__brand-text">
              <div className="app-sidebar__brand-name">ProjectHub</div>
              <div className="app-sidebar__brand-tag">Workspace</div>
            </div>
          )}
        </div>

        <nav className="app-sidebar__nav">
          {navItems.map(({ to, label, iconKey, exact }) => {
            const Icon = iconMap[iconKey] || LayoutDashboard;
            return (
              <NavLink
                key={to}
                to={to}
                end={exact}
                onClick={closeMobileSidebar}
                className={({ isActive }) => `app-sidebar__link${isActive ? ' app-sidebar__link--active' : ''}`}
              >
                <Icon size={18} strokeWidth={2} />
                {showLabels && <span>{label}</span>}
                {to === '/app/chat' && unreadChatCount > 0 && (
                  <span className="app-sidebar__nav-badge">
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </span>
                )}
                {to === '/app/notifications' && unreadNotifCount > 0 && (
                  <span className="app-sidebar__nav-badge">
                    {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {!isMobile && (
          <button type="button" className="app-sidebar__collapse" onClick={handleSidebarToggle}>
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}

        <div className="app-sidebar__footer" style={{ position: 'relative' }}>
          <button
            type="button"
            className="app-sidebar__icon-btn"
            onClick={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
            title="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <div
            className="app-sidebar__profile-block"
            onClick={() => showLabels && setShowProfileMenu(!showProfileMenu)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px', flex: 1,
              cursor: showLabels ? 'pointer' : 'default', padding: '6px',
              borderRadius: '12px', transition: 'background 0.2s', overflow: 'hidden'
            }}
            onMouseEnter={e => { if(showLabels) e.currentTarget.style.background = 'var(--bg-hover)' }}
            onMouseLeave={e => { if(showLabels) e.currentTarget.style.background = 'transparent' }}
          >
            {user?.avatar ? (
              <img src={user.avatar} alt="Profile Avatar" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div className="app-sidebar__avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
            )}

            {showLabels && (
              <div className="app-sidebar__user" style={{ flex: 1 }}>
                <div className="app-sidebar__user-name">{user?.name}</div>
                <div className="app-sidebar__user-role">{user?.role} {user?.title ? `• ${user.title}` : ''}</div>
              </div>
            )}
            {showLabels && <Settings size={14} style={{ color: 'var(--text-dim)', flexShrink: 0, marginRight: 4 }} />}
          </div>

          {showProfileMenu && showLabels && (
             <div className="app-profile-menu" style={{
               position: 'absolute', bottom: '100%', left: 16, right: 16, marginBottom: 8,
               background: 'var(--bg-card)', border: '1px solid var(--border)',
               borderRadius: '12px', padding: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', zIndex: 150,
               boxSizing: 'border-box'
             }}>
               <div style={{ padding: '8px 12px', marginBottom: 4 }}>
                 <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{user?.name}</div>
                 <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{user?.email}</div>
               </div>
               <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
               <button onClick={() => { setShowProfileMenu(false); setShowProfileModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer', borderRadius: '8px', textAlign: 'left', fontSize: 13, fontWeight: 500 }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                 <UserCog size={16} color="var(--accent)" /> Edit Profile
               </button>
               <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', background: 'transparent', border: 'none', color: '#ff4d4f', cursor: 'pointer', borderRadius: '8px', textAlign: 'left', fontSize: 13, fontWeight: 500 }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 77, 79, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                 <LogOut size={16} /> Log Out
               </button>
             </div>
          )}

          {/* Hidden default logout btn to keep layout balance when menu is not active if needed, but since we replaced it with settings in the profile block we can just remove the old logout icon or keep it. I will keep it for collapsed state but let's hide it if not collapsed since menu handles it */}
          {!showLabels && (
            <button type="button" className="app-sidebar__icon-btn" onClick={handleLogout} title="Logout">
              <LogOut size={16} />
            </button>
          )}
        </div>

        <Modal open={showProfileModal} onClose={() => setShowProfileModal(false)} title="Edit Profile">
          <form onSubmit={handleProfileSave}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
              <FormField label="Profile Avatar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {profileForm.avatar || avatarFile ? (
                    <img
                      src={avatarFile ? URL.createObjectURL(avatarFile) : profileForm.avatar}
                      alt="Preview"
                      style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--bg-hover)', border: '1px dashed var(--border-bright)', display: 'grid', placeItems: 'center', color: 'var(--text-dim)', fontSize: 16, fontWeight: 700 }}>
                      {profileForm.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={e => setAvatarFile(e.target.files[0])} style={{ fontSize: 13, color: 'var(--text)', maxWidth: '100%' }} />
                </div>
              </FormField>
              <FormField label="Full Name">
                <input type="text" value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} className="ui-input" required />
              </FormField>
              <FormField label="Email Address">
                <input type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} className="ui-input" required />
              </FormField>
              <FormField label="Job Title">
                <input type="text" value={profileForm.title} onChange={e => setProfileForm(f => ({ ...f, title: e.target.value }))} className="ui-input" placeholder="e.g. Lead Designer" />
              </FormField>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="secondary" onClick={() => setShowProfileModal(false)}>Cancel</Button>
              <Button type="submit" disabled={savingProfile}>{savingProfile ? 'Saving...' : 'Save Profile'}</Button>
            </div>
          </form>
        </Modal>
      </aside>

      <main className="app-main">
        <div className="app-main__content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}